import { useCallback, useEffect, useRef, useState } from 'react';
import type { PaymentChangedPayload } from '@techgitter/contracts';
import { createPayment, type CreatePaymentParams } from '../services/paymentsApi';
import { onSignalREvent } from '../services/signalRService';

export type CreatePaymentPhase =
  | 'idle'
  | 'submitting'
  | 'accepted'
  | 'succeeded'
  | 'failed';

const TIMEOUT_MS = 30_000;

export interface CreatePaymentSuccess {
  paymentId: string;
  rsPaymentId: number | null;
  invoiceNumber: string;
  amount: number;
  newBalance: number;
}

interface UseCreatePaymentResult {
  phase: CreatePaymentPhase;
  result: CreatePaymentSuccess | null;
  error: string | null;
  submit: (
    params: CreatePaymentParams,
    expectedInvoiceNumber: string,
  ) => Promise<void>;
  reset: () => void;
}

// Mirrors useInvoiceCreation: POST to /api/payments, then wait for
// the orchestrator's `payments:changed` SignalR event to confirm RS
// + DV both wrote successfully. Filters the event by the invoice
// *display* number (passed to submit) so concurrent payments on other
// invoices don't resolve this hook.
export function useCreatePayment(): UseCreatePaymentResult {
  const [phase, setPhase] = useState<CreatePaymentPhase>('idle');
  const [result, setResult] = useState<CreatePaymentSuccess | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resolvedRef = useRef(false);
  const expectedInvoiceNumberRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signalRUnsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      signalRUnsubRef.current?.();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    signalRUnsubRef.current?.();
    signalRUnsubRef.current = null;
  }, []);

  const startSignalRListener = useCallback(() => {
    const unsubscribe = onSignalREvent('payments:changed', (data: unknown) => {
      if (resolvedRef.current) return;
      const payload = data as PaymentChangedPayload | undefined;
      if (!payload?.invoiceNumber) return;
      // Filter to the invoice this submit is for so we don't resolve on
      // a sibling payment from another tab / user.
      if (
        !expectedInvoiceNumberRef.current ||
        payload.invoiceNumber !== expectedInvoiceNumberRef.current
      ) {
        return;
      }
      resolvedRef.current = true;
      cleanup();
      setResult({
        paymentId: payload.paymentId,
        rsPaymentId: payload.rsPaymentId,
        invoiceNumber: payload.invoiceNumber,
        amount: payload.amount,
        newBalance: payload.newBalance,
      });
      setPhase('succeeded');
    });
    signalRUnsubRef.current = unsubscribe;
  }, [cleanup]);

  const submit = useCallback(
    async (params: CreatePaymentParams, expectedInvoiceNumber: string) => {
      setPhase('submitting');
      setError(null);
      setResult(null);
      resolvedRef.current = false;
      // Filter the payments:changed event by the invoice *display* number
      // (cr001_number) — that's what the orchestrator broadcasts. NOT
      // params.rsInvoiceId, which is the RS internal invoice id; it never
      // equals the display number, so the old filter never matched and the
      // hook always timed out at 30s even though RS recorded the payment.
      expectedInvoiceNumberRef.current = expectedInvoiceNumber;

      startSignalRListener();

      try {
        await createPayment(params);
        setPhase('accepted');

        timeoutRef.current = setTimeout(() => {
          if (!resolvedRef.current) {
            resolvedRef.current = true;
            cleanup();
            setError(
              'Payment timed out. The payment may still have been recorded — check the invoice in a moment.',
            );
            setPhase('failed');
          }
        }, TIMEOUT_MS);
      } catch (err) {
        cleanup();
        setError(err instanceof Error ? err.message : 'Failed to record payment');
        setPhase('failed');
      }
    },
    [startSignalRListener, cleanup],
  );

  const reset = useCallback(() => {
    resolvedRef.current = true;
    cleanup();
    setPhase('idle');
    setResult(null);
    setError(null);
    expectedInvoiceNumberRef.current = null;
  }, [cleanup]);

  return { phase, result, error, submit, reset };
}
